import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SessionInfo } from "@shared/types";
import { getSession } from "./api-client";

interface SessionContextValue {
  session: SessionInfo | null;
  loading: boolean;
  refresh: () => Promise<void>;
  clear: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined
);

interface SessionProviderProps {
  children: ReactNode;
  /** For tests — bypass bootstrap and inject a value. */
  initialSession?: SessionInfo | null;
  skipBootstrap?: boolean;
}

export function SessionProvider({
  children,
  initialSession = null,
  skipBootstrap = false,
}: SessionProviderProps) {
  const [session, setSession] = useState<SessionInfo | null>(initialSession);
  const [loading, setLoading] = useState<boolean>(!skipBootstrap);

  const refresh = useCallback(async () => {
    try {
      const s = await getSession();
      setSession(s);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setSession(null);
  }, []);

  useEffect(() => {
    if (skipBootstrap) return;
    void refresh();
  }, [refresh, skipBootstrap]);

  const value = useMemo<SessionContextValue>(
    () => ({ session, loading, refresh, clear }),
    [session, loading, refresh, clear]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return ctx;
}
